/**
 * IAMONEAI Streaming Chat Container
 * Real-time streaming chat with token-by-token display
 * 
 * @version 3.0
 * Updated: December 7, 2025
 */

import React, { useState, useRef, useEffect } from 'react';
import { useStreamingChat } from '../hooks/useStreamingChat';
import { TypingIndicator } from './TypingIndicator';
import './StreamingChatContainer.css';

/**
 * Message bubble component
 */
function MessageBubble({ message, isUser, isStreaming }) {
  return (
    <div className={`message ${isUser ? 'message--user' : 'message--ai'} ${isStreaming ? 'message--streaming' : ''}`}>
      {!isUser && (
        <div className="message__avatar">🧠</div>
      )}
      <div className="message__content">
        <div className="message__text">
          {message.text}
          {isStreaming && <span className="cursor-blink">|</span>}
        </div>
        {message.metadata && (
          <div className="message__meta">
            <span className="message__context">{message.metadata.context}</span>
            {message.metadata.timing && (
              <span className="message__timing">{message.metadata.timing}ms</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main streaming chat container
 * 
 * @param {Object} props
 * @param {string} props.iin - User's IIN (required)
 * @param {string} props.context - Current context mode (personal/work/family)
 * @param {function} props.onMessageComplete - Callback when AI response completes
 * @param {function} props.onContextChange - Callback when context changes
 */
export function StreamingChatContainer({ 
  iin, 
  context = 'personal',
  onMessageComplete,
  onContextChange 
}) {
  // Messages state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [currentContext, setCurrentContext] = useState(context);
  
  // Ref for auto-scroll
  const messagesEndRef = useRef(null);
  
  // Update context when prop changes
  useEffect(() => {
    setCurrentContext(context);
  }, [context]);

  // Streaming hook
  const {
    sendMessage,
    currentResponse,
    isTyping,
    metadata,
    error,
    abort,
  } = useStreamingChat({
    iin,
    onMessageComplete: (data) => {
      // Add completed AI message to history
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: data.text,
          isUser: false,
          metadata: {
            intention: data.intention,
            emotion: data.emotion,
            context: data.context,
            timing: data.timing?.total || data.timing,
          },
        },
      ]);
      
      // Handle context switch
      if (data.intention === 'context.switch' && data.context) {
        setCurrentContext(data.context);
        onContextChange?.(data.context);
      }
      
      // Call parent callback
      onMessageComplete?.(data);
    },
    onError: (err) => {
      console.error('Chat error:', err);
      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: `Error: ${err.message}. Please try again.`,
          isUser: false,
          isError: true,
        },
      ]);
    },
  });

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentResponse]);

  // Handle send
  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text) return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text,
        isUser: true,
      },
    ]);

    // Clear input
    setInputValue('');

    // Send to AI
    await sendMessage(text);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="streaming-chat">
      {/* Header */}
      <div className="streaming-chat__header">
        <div className="streaming-chat__title">
          <span className="streaming-chat__icon">🧠</span>
          IAMONEAI
        </div>
        <div className={`streaming-chat__context streaming-chat__context--${currentContext}`}>
          {currentContext === 'personal' && '🏠 Personal'}
          {currentContext === 'work' && '💼 Work'}
          {currentContext === 'family' && '👨‍👩‍👧‍👦 Family'}
        </div>
      </div>

      {/* Messages */}
      <div className="streaming-chat__messages">
        {messages.length === 0 && (
          <div className="streaming-chat__welcome">
            <div className="streaming-chat__welcome-icon">👋</div>
            <h3>Hello!</h3>
            <p>I'm your personal AI guardian. How can I help you today?</p>
            <div className="streaming-chat__suggestions">
              <button onClick={() => setInputValue("What do you know about me?")}>
                What do you know about me?
              </button>
              <button onClick={() => setInputValue("Remind me in 10 minutes to take a break")}>
                Set a reminder
              </button>
              <button onClick={() => setInputValue("Switch to work mode")}>
                Switch to work mode
              </button>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isUser={msg.isUser}
            isStreaming={false}
          />
        ))}

        {/* Streaming response */}
        {isTyping && currentResponse && (
          <MessageBubble
            message={{ text: currentResponse }}
            isUser={false}
            isStreaming={true}
          />
        )}

        {/* Typing indicator (before first token) */}
        {isTyping && !currentResponse && (
          <TypingIndicator context={currentContext} />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="streaming-chat__input-area">
        <textarea
          className="streaming-chat__input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          disabled={isTyping}
        />
        
        {isTyping ? (
          <button 
            className="streaming-chat__btn streaming-chat__btn--abort"
            onClick={abort}
          >
            ⏹ Stop
          </button>
        ) : (
          <button
            className="streaming-chat__btn streaming-chat__btn--send"
            onClick={handleSend}
            disabled={!inputValue.trim()}
          >
            Send →
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="streaming-chat__error">
          {error.message}
        </div>
      )}
    </div>
  );
}

export default StreamingChatContainer;
